import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { defaultMemoize } from 'reselect';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { AdvancedFilterPanel, AdvancedFilterPanelProps } from '~src/spacesPlatform/components/Explorer/AdvancedFilterPanel';
import { IFilterDefinition, ISelectFilterDefinition, ISelectFilterDefinitionOption } from '~src/spacesPlatform/services/FilterPanelService';
import { updateTotalUserCountForServerUserSpace } from '~src/userManagement/sagas/ServerUserSpaceSagas';
import { selectMaxSiteRoleCountsForServerUserSpace } from '~src/userManagement/selectors/MaxSiteRoleForServerUserSpaceSelectors';

import { SiteRoleFilterHelper } from '../../../../siteManagement/helpers/SiteRoleFilterHelper';
import { IStringValuedFilterClause } from '../../../../spacesPlatform/models/IFilterClause';
import { ServerUsers } from '../../../services/resources/ServerUsers';
import { SiteRolesResource } from '../../../services/resources/SiteRoles';
import { ServerUserDefinition } from '../../contentDefinition/ServerUserDefinition';


export type ServerUsersAdvancedFilterPanelProps = ServerUsersAdvancedFilterPanelOwnProps & ServerUsersAdvancedFilterPanelStateProps & ServerUsersAdvancedFilterPanelDispatchProps;
export interface ServerUsersAdvancedFilterPanelOwnProps extends AdvancedFilterPanelProps {}

export interface ServerUsersAdvancedFilterPanelStateProps {
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[];
}

export interface ServerUsersAdvancedFilterPanelDispatchProps {
  updateTotalUserCountForServerUserSpace?: (maxSiteRole?: string, maxSiteRoleCounts?: ISelectFilterDefinitionOption<string>[]) => void;
}

export interface ServerUsersAdvancedFilterPanelState {
  siteRoleOptionsWithCountsWithSolr: ISelectFilterDefinitionOption<string>[];
}

// This function is a weird way to update filter definitions asynchronously
// since those definitions are created in the child component - AdvancedFilterPanel.
// See related comment there.
function transformFilterDefinitions(
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[]
): (filterDefinitions: IFilterDefinition<any>[]) => void {
  return (filterDefinitions: IFilterDefinition<any>[]) => {
    if (siteRoleOptionsWithCounts) {
      (filterDefinitions[0] as ISelectFilterDefinition<string>).options = siteRoleOptionsWithCounts;
    }
  };
}

export class ServerUsersAdvancedFilterPanel extends React.Component<ServerUsersAdvancedFilterPanelProps, ServerUsersAdvancedFilterPanelState> {

  private isUnmounted = false;
  private siteRoles: SiteRolesResource = undefined;
  private memoizedTransformFilterDefinitions = defaultMemoize(transformFilterDefinitions);

  public constructor(props: ServerUsersAdvancedFilterPanelProps) {
    super(props);
    this.siteRoles = SiteRolesResource.instance;
    this.state = { siteRoleOptionsWithCountsWithSolr: null };
  }

  public render() {
    const transformFilterDefinitions = this.memoizedTransformFilterDefinitions(
      FeatureFlags.serverUserSolrMigrationEnabled ? this.props.siteRoleOptionsWithCounts : this.state.siteRoleOptionsWithCountsWithSolr
    );
    return <AdvancedFilterPanel {...this.props} transformFilterDefinitions={transformFilterDefinitions} />;
  }

  public componentDidMount() {
    this.updateSiteRoleCounts();
  }

  public componentDidUpdate(prevProps: ServerUsersAdvancedFilterPanelProps) {
    if (this.searchTerm(this.props) !== this.searchTerm(prevProps)) {
      this.updateSiteRoleCounts();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
  }

  private updateSiteRoleCounts = async () => {
    if (FeatureFlags.serverUserSolrMigrationEnabled) {
      let siteRoleOptionsWithCounts = await ServerUsers.instance.getUserCountsByMaxSiteRoleWithoutSolr(this.props.mixedContentFilterMap);
      this.props.updateTotalUserCountForServerUserSpace(undefined, siteRoleOptionsWithCounts);
    } else {
      let query = ServerUserDefinition.getBasicQuery();
      if (this.searchTerm(this.props)) {
        query = query.addFilter(this.props.mixedContentFilterMap.search);
      }
      const userCountsByRole = await ServerUsers.instance.getUserCountsByMaxSiteRole(query);
      let siteRoleOptionsWithCountsWithSolr = SiteRoleFilterHelper.siteRoleOptions(this.siteRoles.optionsForCurrentUser(), userCountsByRole);
      !this.isUnmounted && this.setState({ siteRoleOptionsWithCountsWithSolr });
    }
  };

  private searchTerm = (props: ServerUsersAdvancedFilterPanelProps) => {
    return (props.mixedContentFilterMap.search
      && (props.mixedContentFilterMap.search as IStringValuedFilterClause).value)
      || '';
  };
}

const mapDispatchToProps: MapDispatchToProps<ServerUsersAdvancedFilterPanelDispatchProps> = (dispatch) => bindActionCreators({
  updateTotalUserCountForServerUserSpace: updateTotalUserCountForServerUserSpace
}, dispatch);

const mapStateToProps: MapStateToProps<ServerUsersAdvancedFilterPanelStateProps> = (state: AppState) => ({
  siteRoleOptionsWithCounts: selectMaxSiteRoleCountsForServerUserSpace(state)
});

export const ConnectedServerUsersAdvancedFilterPanel: React.ComponentClass<ServerUsersAdvancedFilterPanelOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ServerUsersAdvancedFilterPanel);
